import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { ConnectivityService } from '../offline/connectivity.service';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly updates = inject(SwUpdate, { optional: true });
  private readonly connectivity = inject(ConnectivityService);
  private readonly installEvent = signal<InstallPromptEvent | null>(null);

  readonly updateReady = signal(false);
  readonly online = toSignal(this.connectivity.online$, {
    initialValue: this.connectivity.isOnline(),
  });
  readonly canInstall = computed(() => this.installEvent() !== null);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        this.installEvent.set(event as InstallPromptEvent);
      });
      window.addEventListener('appinstalled', () => this.installEvent.set(null));
    }

    const updates = this.updates;
    if (!updates?.isEnabled) {
      return;
    }

    updates.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.updateReady.set(true);
      }
    });
    void updates.checkForUpdate().catch(() => undefined);
  }

  async install(): Promise<void> {
    const event = this.installEvent();
    if (!event) {
      return;
    }
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === 'accepted') {
      this.installEvent.set(null);
    }
  }

  async activateUpdate(): Promise<void> {
    if (!this.updates?.isEnabled) {
      return;
    }
    await this.updates.activateUpdate();
    document.location.reload();
  }
}
